declare var navigator: any

import { inject, Injectable, OnDestroy } from '@angular/core'
import {
  BehaviorSubject,
  from,
  fromEvent,
  Observable,
  Subject,
  Subscription,
} from 'rxjs'
// import { IJobSummary } from '../../types/infini-job.interface';
// import { StorageService } from '@app/services/storage';
import { AlertController, Platform } from '@ionic/angular'
// import * as moment from 'moment';
// import { ConnectionStatus, Network } from '@capacitor/network';
import {
  debounceTime,
  first,
  map,
  mergeMap,
  takeUntil,
  distinctUntilChanged,
} from 'rxjs/operators'
import { WrappedSocket, WrappedSocketEventTypes } from './utils/wrapped-socket'
import { H2HMessage, H2HConversation } from '../types/message'
import { AuthService } from './auth.service'
import { environment } from 'src/environments/environment'
import { MessageService } from './message.service'

interface ConnectionStatus {
  connected: boolean
}
const WS_URL = environment.wsUrl
@Injectable({
  providedIn: 'root',
})
export class SocketService implements OnDestroy {
  //@ts-ignore
  private readonly messageSubject: BehaviorSubject<H2HMessage | null> =
    new BehaviorSubject(null)
  public readonly latestMessage: Observable<H2HMessage | null> =
    this.messageSubject.asObservable()

  // Subject for unread count changes - emits count from socket events
  private readonly unreadCountSubject = new Subject<number>()
  public readonly unreadCountChanged$: Observable<number> =
    this.unreadCountSubject.asObservable().pipe(distinctUntilChanged())

  private readonly subs: Subscription[] = []
  private socket: WrappedSocket | null = null
  private readonly killer: Subject<boolean> = new Subject()

  private authService = inject(AuthService)
  private messageService = inject(MessageService)

  constructor(
    private readonly platform: Platform,
    private alertController: AlertController,
  ) {}

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe())

    if (navigator.connection?.removeAllListeners) {
      navigator.connection.removeAllListeners()
    }

    this.killer.next(true)
    this.killer.complete()
  }

  /**
   * Check if WebSocket is currently connected
   */
  isConnected(): boolean {
    return (
      this.socket !== null &&
      this.socket !== undefined &&
      this.socket.isConnected()
    )
  }

  async emit(data: any): Promise<void> {
    if (!this.socket) {
      await this.initSocketConnection()
    }

    if (this.socket) {
      return this.socket.send(data)
    } else {
      throw new Error('WebSocket not available')
    }
  }

  init(): void {
    this.initSocketConnection()

    this.subs.push(
      this.platform.resume.subscribe(() => {
        this.initSocketConnection()
      }),
    )

    const actionEventNames = [
      'hidden',
      'webkitHidden',
      'visibilitychange',
      'webkitvisibilitychange',
      'msvisibilitychange',
    ]
    this.subs.push(
      from(actionEventNames)
        .pipe(
          mergeMap((eventName) =>
            fromEvent(document, eventName).pipe(
              takeUntil(this.killer),
              map((evt) => {
                return {
                  name: eventName,
                  evt,
                }
              }),
            ),
          ),
          debounceTime(500),
        )
        .subscribe((evt: any) => {
          if (
            evt.evt.target?.hidden !== undefined &&
            typeof evt.evt.target?.hidden === 'boolean'
          ) {
            if (evt.evt.target?.hidden) {
            } else {
              this.initSocketConnection()
            }
          }
        }),
    )

    navigator.connection?.addEventListener(
      'networkStatusChange',
      (status: any) => {
        if (!status.connected) {
          if (this.socket) {
            this.socket.disconnect()
            this.socket = null
          }
        } else {
          if (!this.socket) this.initSocketConnection()
        }
        return null
      },
    )
  }

  private async initSocketConnection(): Promise<void> {
    this.socket = new WrappedSocket({
      url: `${WS_URL}?accessToken=${this.authService.accessToken}`,
      autoConnect: true,
    })
    this.socket.on(WrappedSocketEventTypes.MESSAGE, (data: any) => {
      if (data?.conversationUuid && data?.message) {
        this.messageSubject.next(data)
        // When a new message arrives, calculate and emit updated unread count
        this.updateUnreadCountFromMessage(data)
      }

      if (data?.event == 'notify_user') {
        this.alertController
          .create({
            cssClass: 'my-custom-class',
            header: `Error`,
            message: data?.message,
            buttons: [
              {
                text: 'Ok',
                role: 'dismiss',
              },
            ],
          })
          .then(async (alert) => {
            await alert.present()
            await alert.onWillDismiss()
          })
      }
    })
    this.socket.on(WrappedSocketEventTypes.DISCONNECT, () => {
      this.initSocketConnection()
    })
  }

  /**
   * Calculate unread count from incoming message and emit it
   * This provides real-time updates to the message badge without polling
   */
  private updateUnreadCountFromMessage(data: any): void {
    const userUuid = this.authService.getUuid()
    if (!userUuid) return

    // Only emit if the message is from another user (not our own message)
    if (data?.message?.senderUuid === userUuid) return

    // Get current conversations to calculate total unread count
    this.messageService.getMyConversations().subscribe({
      next: (conversations: H2HConversation[]) => {
        const unreadCount = this.calculateUnreadCount(conversations, userUuid)
        this.unreadCountSubject.next(unreadCount)
      },
      error: (err) => {
        console.error('Error fetching conversations for unread count:', err)
      },
    })
  }

  /**
   * Calculate total unread message count from conversations
   */
  private calculateUnreadCount(
    conversations: H2HConversation[],
    userUuid: string,
  ): number {
    if (!conversations) return 0

    return conversations.reduce((total, conversation) => {
      // Skip welcome message conversation
      if (conversation.uuid === 'welcome-message') return total

      const unreadInConvo =
        conversation.messages?.filter((m) => {
          // Import the isUnread utility
          const isUnread = m.read === false || m.read === 'false'
          return isUnread && m.senderUuid !== userUuid
        }).length || 0

      return total + unreadInConvo
    }, 0)
  }

  /**
   * Public method to notify about unread count changes
   * Call this after marking messages as read to update the badge dynamically
   */
  public notifyUnreadCountChanged(): void {
    const userUuid = this.authService.getUuid()
    if (!userUuid) {
      console.warn(
        '[SocketService] No userUuid available to calculate unread count',
      )
      return
    }

    console.log(
      '[SocketService] notifyUnreadCountChanged() called - fetching updated conversations',
    )

    // Fetch latest conversations and calculate unread count
    this.messageService.getMyConversations().subscribe({
      next: (conversations: H2HConversation[]) => {
        const unreadCount = this.calculateUnreadCount(conversations, userUuid)
        console.log(
          '[SocketService] Calculated unread count:',
          unreadCount,
          'from',
          conversations.length,
          'conversations',
        )
        this.unreadCountSubject.next(unreadCount)
      },
      error: (err) => {
        console.error(
          '[SocketService] Error fetching conversations for unread count:',
          err,
        )
      },
    })
  }
}
